/**
 * The sun light and its cascaded shadow maps.
 *
 * Milestone 0 shipped a single orthographic cascade with texel snapping and a comment promising
 * the rest "when there is something at 4 km to receive them". There is now, so this is the real
 * thing: `CSMShadowNode`, three's WebGPU cascaded-shadow implementation, driven by splits chosen
 * for the one thing that makes shadows hard in a flight simulator.
 *
 * ## Why flight is the hard case for shadow maps
 *
 * A shadow map is a fixed budget of texels spread over a volume. Most renderers only have to
 * cover a character-sized subject and a room-sized environment — two orders of magnitude. Here
 * the *same frame* contains an aircraft eight metres long whose gear legs cast shadows a
 * pilot notices on the flare, and a ridge line eight kilometres away whose shadow is the main
 * thing giving the mountains shape. That is three orders of magnitude, and no single cascade
 * can serve both: fit it to the aeroplane and the mountains have no shadows at all; fit it to
 * the mountains and each shadow texel is metres across, so the aircraft's own shadow is a grey
 * smudge that visibly jumps as it moves.
 *
 * Hence the custom splits below rather than the usual logarithmic or "practical" scheme. Those
 * schemes are derived from the camera's near plane, and this camera's near plane is centimetres
 * from a chase view — which drags the first cascade down to a volume the size of a wingtip and
 * wastes it. The breaks here are chosen from what is *in* the scene rather than from the
 * projection matrix: an aircraft-scale cascade, a circuit-scale one, and the rest for terrain.
 *
 * ## Shimmer
 *
 * The classic artefact: a shadow frustum that moves continuously with the camera makes every
 * shadow edge crawl and boil, which reads as low quality no matter what the resolution is.
 * `CSMShadowNode` snaps each cascade's centre to its own shadow-texel grid, which fixes it —
 * the same trick the M0 stub did by hand. What it does *not* do by itself is keep the frustum
 * from resizing, which is why the cascade extents here are derived from fixed distances rather
 * than from a fit to visible geometry: a frustum that grows and shrinks re-quantises every texel
 * and shimmers even when it is snapped.
 *
 * `updateFrame(alpha)`: yes — the light rig follows the focus point each frame so the cascades
 * are built around the aircraft. The cascade fitting itself is `CSMShadowNode.updateBefore`,
 * which three calls during the render. Allocation-free: all vector maths goes through the
 * preallocated scratch objects.
 */

import { Color, DirectionalLight, Object3D, Vector3 } from 'three';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';
import type { QualitySettings } from '../core/quality.js';
import { scratch } from '../core/math.js';

/**
 * Cascade break fractions, as a proportion of the shadow distance.
 *
 * Read them as: with four cascades over 8 km, the first covers the nearest 60 m — the aircraft
 * and the runway under it — the second out to 560 m, the third to 2.6 km, and the last takes the
 * remaining terrain. Each is a factor of roughly four to five larger than the last, which is the
 * right ratio: much less and the far cascades run out of range, much more and the quality step
 * between them becomes visible as a line across the ground.
 */
const CASCADE_BREAKS: Readonly<Record<number, readonly number[]>> = Object.freeze({
  1: [1],
  2: [0.05, 1],
  3: [0.02, 0.16, 1],
  4: [0.0075, 0.07, 0.32, 1],
});

export class SunShadow {
  readonly light: DirectionalLight;
  /** The light's target; keep it in the scene graph or the direction is ignored. */
  readonly target: Object3D;

  /** Direction *toward* the sun, normalised. */
  private readonly sunDirection = new Vector3(0.4, 0.6, 0.2).normalize();
  private radiusM: number;
  private mapSize: number;
  private cascadeCountValue: number;
  private enabled: boolean;
  private csm: CSMShadowNode | null = null;

  constructor(settings: QualitySettings) {
    this.radiusM = settings.shadows.distanceM;
    this.mapSize = settings.shadows.mapSize;
    this.cascadeCountValue = settings.shadows.cascades;
    this.enabled = settings.shadows.enabled;

    this.light = new DirectionalLight(new Color(0xffffff), 1);
    this.light.name = 'sun';
    this.light.castShadow = this.enabled;
    this.light.shadow.mapSize.setScalar(this.mapSize);
    /*
     * Depth bias. Negative pushes the comparison depth away from the light, which is what stops
     * a surface shadowing itself ("shadow acne") on ground that is nearly edge-on to the sun —
     * exactly the situation at dawn and dusk, over every slope. `CSMShadowNode` multiplies this
     * by the cascade index, so the far cascades — whose texels cover far more ground and so have
     * far more depth slope inside one texel — get proportionally more, automatically.
     */
    this.light.shadow.bias = -0.0004;
    this.light.shadow.normalBias = 0.035;

    this.target = new Object3D();
    this.target.name = 'sun-target';
    this.light.target = this.target;

    this.rebuildCascades();
  }

  /** Cascades the active tier asks for, and the number actually rendered. */
  get cascadeCount(): number {
    return this.cascadeCountValue;
  }

  /** Direction toward the sun (read-only view of the internal vector). */
  get direction(): Vector3 {
    return this.sunDirection;
  }

  setSunDirection(direction: Vector3): void {
    this.sunDirection.copy(direction).normalize();
  }

  /**
   * The sun's colour and strength.
   *
   * `colour` is the atmospheric transmittance from `render/environment.ts`, so this is the same
   * orange the sun's disc is drawn with — the light on the terrain and the light in the sky can
   * never disagree, because they are the same number.
   */
  setSunRadiance(r: number, g: number, b: number): void {
    // Three's physically-correct lighting takes `color × intensity` as irradiance and applies
    // the 1/π of the Lambertian BRDF itself, so with a solar irradiance normalised to 1 the
    // transmittance *is* the light. No fudge factor, which is the point of doing it this way.
    this.light.color.setRGB(r, g, b);
    this.light.intensity = 1;
  }

  /** Re-read the tier: shadow resolution, range, cascade count and on/off. */
  applyQuality(settings: QualitySettings): void {
    const cascadesChanged = settings.shadows.cascades !== this.cascadeCountValue;
    const mapSizeChanged = settings.shadows.mapSize !== this.mapSize;
    const distanceChanged = settings.shadows.distanceM !== this.radiusM;

    this.enabled = settings.shadows.enabled;
    this.radiusM = settings.shadows.distanceM;
    this.cascadeCountValue = settings.shadows.cascades;
    this.mapSize = settings.shadows.mapSize;
    this.light.castShadow = this.enabled;
    this.light.shadow.mapSize.setScalar(this.mapSize);

    // The cascade shadow maps are cloned from `light.shadow` when the node is built, so any of
    // these three changing means the existing node is stale and has to be rebuilt rather than
    // reconfigured. It is a handful of allocations on a tier change, which happens seconds apart
    // at worst, and it is the difference between a tier step being correct and being cosmetic.
    if (cascadesChanged || mapSizeChanged || distanceChanged || this.csm === null) {
      this.rebuildCascades();
    }
  }

  private rebuildCascades(): void {
    this.csm?.dispose();
    this.csm = null;
    // `undefined`, not `null`: three's check is `!== undefined`, so a null here would be
    // taken for a live custom shadow node and passed straight to `nodeObject()`.
    this.light.shadow.shadowNode = undefined;

    if (!this.enabled) return;

    const breaks = CASCADE_BREAKS[this.cascadeCountValue] ?? CASCADE_BREAKS[3];
    const csm = new CSMShadowNode(this.light, {
      cascades: this.cascadeCountValue,
      maxFar: this.radiusM,
      mode: 'custom',
      // Reach behind the near plane of each cascade so a mountain *outside* the view frustum
      // still casts into it. Scaled with the shadow distance because the things doing the
      // casting scale with it too — a 200 m margin is generous for a circuit and useless when
      // the far cascade is eight kilometres deep.
      lightMargin: Math.max(200, this.radiusM * 0.25),
      customSplitsCallback: (cascades: number, _near: number, _far: number, target: number[]) => {
        const table = CASCADE_BREAKS[cascades] ?? breaks;
        target.length = 0;
        for (const value of table) target.push(value);
      },
    });
    /*
     * Blend the cascade boundaries.
     *
     * Without it there is a hard line across the ground where one cascade hands over to the
     * next, and because the cascades are fitted to the camera that line *moves with the camera*
     * — a shadow-quality edge sliding across the terrain, which is far more distracting than
     * the lower resolution it is hiding. The blend costs a second shadow lookup in the overlap
     * band only.
     */
    csm.fade = true;

    this.light.shadow.shadowNode = csm;
    this.csm = csm;
  }

  /**
   * Keep the light rig with the focus point.
   *
   * With CSM the cascade volumes are fitted by the node itself, so all this has to establish is
   * the light's *direction* — which three takes as `target.position − light.position`. The rig
   * still follows the aircraft so those two points stay in a sane numeric range near the
   * floating origin rather than drifting off to where fp32 stops being precise.
   *
   * Allocation-free — uses `scratch.v3a`/`v3b`, which it clobbers.
   */
  updateFrame(focus: Vector3): void {
    if (!this.enabled) return;

    const snapped = scratch.v3a.copy(focus);
    this.target.position.copy(snapped);
    this.target.updateMatrixWorld();

    this.light.position
      .copy(scratch.v3b.copy(this.sunDirection).multiplyScalar(Math.max(1_000, this.radiusM * 2)))
      .add(snapped);
    this.light.updateMatrixWorld();
  }

  dispose(): void {
    this.csm?.dispose();
    this.csm = null;
    this.light.shadow.map?.dispose();
    this.light.dispose();
  }
}
